import { useSetState } from './store/use-set-state.js';
import { LegendItem } from '../../../core/components/legend/categorical/types/legend.js';
import {
  CustomColorPalette,
  ColorPalette,
} from '../../../core/types/color-palette.js';
import { MultiMeterBarSegmentProps } from '../types/multi-meter-bar-chart.js';
import {
  MultiMeterBarChartState,
  MultiMeterBarChartSegmentData,
} from '../types/state.js';
import { buildSegmentData } from '../utils/build-segment-data.js';

export function useMultiMeterBarStore() {
  const setState = useSetState();

  const initSegmentsState = (
    data: MultiMeterBarSegmentProps[],
    colorPalette: ColorPalette | CustomColorPalette,
  ): Omit<MultiMeterBarChartState, 'tooltip'> => {
    const segments = data.map((d, i) => buildSegmentData(d, colorPalette, i));

    const visibleSegments = new Map(
      segments.map((segment) => [
        segment.id,
        { ...segment, highlighted: true },
      ]),
    );

    return { segments, visibleSegments };
  };

  const setVisibleSegments = (visibleSegments: LegendItem[]) => {
    setState((prev) => {
      const { segments, visibleSegments: visibleSegmentsStore } = prev;

      const segmentsWithIds: Record<string, MultiMeterBarChartSegmentData> =
        segments.reduce(
          (acc, curr) => ({ ...acc, [curr.id]: { ...curr } }),
          {},
        );

      visibleSegmentsStore.clear();

      visibleSegments.forEach((segment) =>
        visibleSegmentsStore.set(segment.id, {
          ...segmentsWithIds[segment.id],
        }),
      );

      return {
        ...prev,
        visibleSegments: visibleSegmentsStore,
      };
    });
  };

  const setHighlightedSegments = (highlightedSegments?: LegendItem[]) => {
    setState((prev) => ({ ...prev, highlightedSegments }));
  };

  return {
    initSegmentsState,
    setHighlightedSegments,
    setVisibleSegments,
  };
}
